import type { AxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth.context';
import { toast } from '@/hooks/use-toast';
import { getApiErrorMessage } from '@/lib/api-errors';
import { ACCOUNT_CLOSED_LOGIN_ROUTE } from '@/lib/auth-routes';
import { accountService, type DeleteAccountPayload } from '@/services/account.service';

/**
 * "Excluir conta" (chamado 7116): o resumo do ciente (regra do backend) e a
 * exclusão. Excluída a conta, a sessão acaba e o login mostra o aviso.
 */
export function useAccountDeletion(enabled: boolean) {
  const { logout } = useAuth();

  const { data: summary = null } = useQuery({
    queryKey: ['account-deletion'],
    queryFn: () => accountService.getAccountDeletion(),
    enabled,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: DeleteAccountPayload) => accountService.deleteMyAccount(payload),
    onSuccess: () => logout({ redirectTo: ACCOUNT_CLOSED_LOGIN_ROUTE }),
    onError: (error: unknown) => {
      const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
      toast({
        title: 'Não foi possível excluir a conta',
        description: getApiErrorMessage(detail, 'Tente novamente em alguns instantes.'),
        variant: 'destructive',
      });
    },
  });

  return { summary, deleteAccount: mutate, isDeleting: isPending };
}
